import React, { useEffect, useState } from "react";
import { PlayCircle, MousePointerClick, ShoppingBag, Lock, Flag, BellRing } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { api } from "../lib/api";

const COPY = {
  pt: { title: "Painel da Demo", sub: "Demonstração interativa — últimos {d} dias", range: "Período", starts: "Inícios da demo", cta: "Cliques em Experimentar", buys: "Cliques em Criar meu Natal", locked: "Tentativas bloqueadas", ends: "Chegaram ao fim (10 min)", reminder: "Lembrete pós-demo (visto / clicado)", areas: "Áreas mais visitadas", markets: "Inícios por mercado", sources: "Origem dos cliques de compra", lockedActions: "Ações bloqueadas mais tentadas", daily: "Inícios e compras por dia", rate1: "CTA → início", rate2: "início → compra", rate3: "concluíram", empty: "Ainda sem dados neste período.", views: "visualizações" },
  en: { title: "Demo Dashboard", sub: "Interactive demo — last {d} days", range: "Period", starts: "Demo starts", cta: "Try-it clicks", buys: "Create my Christmas clicks", locked: "Locked attempts", ends: "Reached the end (10 min)", reminder: "Post-demo reminder (seen / clicked)", areas: "Most visited areas", markets: "Starts by market", sources: "Buy-click sources", lockedActions: "Most attempted locked actions", daily: "Starts & buys per day", rate1: "CTA → start", rate2: "start → buy", rate3: "completed", empty: "No data in this period yet.", views: "views" },
};
const AREA_KEY = { dashboard: "nav.home", decoration: "nav.decoration", ceia: "nav.ceia", recipes: "nav.recipes", table: "nav.table", planner: "nav.planner", gifts: "nav.gifts", budget: "nav.budget", "secret-santa": "nav.secretsanta" };

function Bars({ rows, label, testid, t }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="glass rounded-2xl p-5" data-testid={testid}>
      <p className="mb-4 text-xs uppercase tracking-wider text-xmas-slate">{label}</p>
      {!rows.length && <p className="text-sm text-xmas-slate/70">—</p>}
      <ul className="space-y-2.5">
        {rows.map((r) => (
          <li key={r.key}>
            <div className="mb-1 flex justify-between text-sm"><span className="text-xmas-snow">{AREA_KEY[r.key] ? t(AREA_KEY[r.key]) : r.key}</span><span className="font-mono-num text-xmas-gold">{r.count}</span></div>
            <div className="h-1.5 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-xmas-gold/80" style={{ width: `${(r.count / max) * 100}%` }} /></div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function DemoMetrics() {
  const { t, lang } = useI18n();
  const C = COPY[lang === "pt" || lang === "pt-PT" ? "pt" : "en"];
  const [days, setDays] = useState(30);
  const [m, setM] = useState(null);
  useEffect(() => { api.get(`/admin/demo-metrics?days=${days}`).then(({ data }) => setM(data)).catch(() => setM({})); }, [days]);
  if (!m) return null;
  const kpis = [
    { icon: MousePointerClick, label: C.cta, value: m.cta_clicks },
    { icon: PlayCircle, label: C.starts, value: m.starts },
    { icon: ShoppingBag, label: C.buys, value: m.buy_clicks, gold: true },
    { icon: Lock, label: C.locked, value: m.locked_attempts },
    { icon: Flag, label: C.ends, value: m.ends },
    { icon: BellRing, label: C.reminder, value: `${m.reminder_views ?? 0} / ${m.reminder_ctas ?? 0}` },
  ];
  const daily = m.daily || []; const dmax = Math.max(1, ...daily.map((d) => d.starts));
  return (
    <section className="mt-12" data-testid="admin-demo-metrics">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div><h2 className="font-display text-xl text-xmas-snow">{C.title}</h2><p className="text-xs text-xmas-slate">{C.sub.replace("{d}", m.days ?? days)}</p></div>
        <div className="flex gap-1 rounded-full border border-xmas-gold/20 p-1 text-xs">{[7, 30, 90].map((d) => <button key={d} onClick={() => setDays(d)} data-testid={`demo-metrics-range-${d}`} className={`rounded-full px-3 py-1 transition ${days === d ? "bg-xmas-gold text-xmas-navy font-bold" : "text-xmas-slate hover:text-xmas-snow"}`}>{d}d</button>)}</div>
      </div>
      <div className="mb-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {kpis.map((k, i) => (
          <div key={i} className={`glass rounded-2xl p-5 ${k.gold ? "border border-xmas-gold/40" : ""}`} data-testid={`demo-kpi-${i}`}>
            <k.icon className="mb-3 h-5 w-5 text-xmas-gold" />
            <p className="font-mono-num text-2xl font-bold text-xmas-snow">{k.value ?? 0}</p>
            <p className="mt-1 text-[11px] uppercase tracking-wider text-xmas-slate">{k.label}</p>
          </div>
        ))}
      </div>
      <div className="mb-4 flex flex-wrap gap-x-6 gap-y-1 text-xs text-xmas-slate" data-testid="demo-rates">
        <span>{C.rate1}: <b className="text-xmas-gold">{m.rates?.cta_to_start ?? 0}%</b></span><span>{C.rate2}: <b className="text-xmas-gold">{m.rates?.start_to_buy ?? 0}%</b></span><span>{C.rate3}: <b className="text-xmas-gold">{m.rates?.completed ?? 0}%</b></span>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Bars rows={m.areas || []} label={C.areas} testid="demo-areas" t={t} />
        <Bars rows={m.markets || []} label={C.markets} testid="demo-markets" t={t} />
        <Bars rows={m.buy_sources || []} label={C.sources} testid="demo-buy-sources" t={t} />
        <Bars rows={m.locked_actions || []} label={C.lockedActions} testid="demo-locked-actions" t={t} />
      </div>
      <div className="glass mt-4 rounded-2xl p-5" data-testid="demo-daily">
        <p className="mb-4 text-xs uppercase tracking-wider text-xmas-slate">{C.daily}</p>
        {!daily.length ? <p className="text-sm text-xmas-slate/70">{C.empty}</p> : (
          <div className="flex h-32 items-end gap-1 overflow-x-auto">
            {daily.map((d) => (
              <div key={d.day} className="group relative flex min-w-[14px] flex-1 flex-col items-center justify-end gap-0.5" title={`${d.day}: ${d.starts} / ${d.buys}`}>
                <div className="w-full rounded-t bg-xmas-gold/70" style={{ height: `${(d.starts / dmax) * 100}%`, minHeight: d.starts ? 3 : 0 }} />
                {d.buys > 0 && <div className="w-full rounded-t bg-xmas-crimson" style={{ height: `${(d.buys / dmax) * 100}%`, minHeight: 3 }} />}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
